import { useMemo } from 'react'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import { Close, KeyboardAltOutlined, MoreVert } from '@mui/icons-material'
import useOverlay from '@/hooks/useOverlay'
import useValidationNavList from '@/hooks/useValidationNavList'
import {
  useGetDocument,
  useUpdateDocument,
} from '@/service-library/hooks/documents'
import { getShortcuts } from '@/utils/document-overlay'
import { showErrorSnackbar } from '@/utils/snackbars'
import DocumentActionsMenu from '@/components/document-actions-menu/DocumentActionsMenu'
import EditableDocumentName from '@/components/editable-document-name/EditableDocumentName'
import KeyboardShortcutsDialog from '@/components/keyboard-shortcuts-popover/KeyboardShortcutsDialog'
import DocumentNavigationButtons from '@/components/validation-overlay/DocumentNavigationButtons'

type DocumentOverlayHeaderProps = {
  documentQuery: ReturnType<typeof useGetDocument>
  documentView: 'processing' | 'idle'
  onClose: () => void
} & Omit<ReturnType<typeof useValidationNavList>, 'nextDocumentId'>

export default function DocumentOverlayHeader({
  documentView,
  documentQuery,
  onClose,
  ...props
}: DocumentOverlayHeaderProps) {
  const actionsMenuOverlay = useOverlay()
  const keyboardShortcutsOverlay = useOverlay()
  const { document, queryKey } = documentQuery

  const { updateDocument } = useUpdateDocument({
    detailQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Failed to update document.')
    },
  })

  const keyboardShortcuts = useMemo(
    () => getShortcuts(`lite-${documentView}`),
    [documentView],
  )

  return (
    <Box sx={{ p: 1, px: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={document ? 'space-between' : 'flex-end'}
        sx={{ minHeight: 40 }} // minHeight so it doesn't change height when switching to editing for name
      >
        {document && (
          <EditableDocumentName
            document={document}
            updateDocument={updateDocument}
          />
        )}

        {/* MARK: Keyboard Shortcuts Button / More Menu / Field Details Button */}
        <Stack direction="row" spacing={0.25} alignItems="center">
          <DocumentNavigationButtons {...props} />
          {keyboardShortcuts.length > 0 && (
            <Tooltip
              title="Shortcuts"
              arrow
              disableInteractive
              enterDelay={1000}
            >
              <IconButton
                onClick={() => {
                  keyboardShortcutsOverlay.open()
                }}
              >
                <KeyboardAltOutlined />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={actionsMenuOverlay.open}
            disabled={!document}
          >
            <MoreVert />
          </IconButton>

          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>

          {document && (
            <DocumentActionsMenu
              basicOptions
              projectId={document.project_id}
              documentId={document.id}
              overlay={actionsMenuOverlay}
              onDelete={onClose}
            />
          )}
        </Stack>
      </Stack>

      <KeyboardShortcutsDialog
        overlay={keyboardShortcutsOverlay}
        keyboardShortcutGroups={keyboardShortcuts}
      />
    </Box>
  )
}
